import { ApplicationRef } from '@angular/core';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { EnaioUtils } from 'src/modules/enaio/misc/enaio-utils';
import { EnaioScriptType } from 'src/modules/enaio/shared/EnaioScriptType';
import { EnaioUserIdentification } from 'src/modules/enaio/shared/EnaioUserIdentification';
import { EnaioWorkItem } from 'src/modules/enaio/shared/EnaioWorkItem';
import { EnaioWorkflowDefinition } from 'src/modules/enaio/shared/EnaioWorkflowDefinition';
import { EnaioWorkflowEventDefinition } from 'src/modules/enaio/shared/EnaioWorkflowEventDefinition';
import { EnaioWorkflowEventType } from 'src/modules/enaio/shared/EnaioWorkflowEventType';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FileUploadService } from 'src/modules/sm-base/services/file-upload-service';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendEnaioMapper } from '../../enaio/models/frontend-enaio-mapper.model';
import { FormHelper } from './form-helper.model';
import { WfPortalInterface } from './wf-portal-interface.model';
import { EnaioCallCompleteWorkItem } from 'src/modules/enaio/shared/EnaioCallCompleteWorkItem';

export class WorkflowForm {

    form: FrontendFormDefinition;
    clientScriptFunctions: OrdinaryObject<Function> = {};
    scriptFormHelper: FormHelper;
    scriptGlobals: OrdinaryObject;
    scriptScriptingStorage: OrdinaryObject;

    constructor(public workItem: EnaioWorkItem, public workflowModel: EnaioWorkflowDefinition, public enaioUserName: string,
        public app: MainAppService, public fileUpload: FileUploadService, public applicationRef: ApplicationRef) {
    }

    async init(): Promise<void> {
        let mask = this.workItem.masks[0];
        this.form = FrontendEnaioMapper.enaioToFrontendForm(mask.fields.map(field => field.guiField), this.app, 
            "wfs.families." + this.workflowModel.familyPackage.name + ".fields", this.workItem.parameters, true, this.app.isMobile(), mask.width, mask.height);

        let allFields = this.form.getAllFields();
        for (let param of this.workItem.parameters) {
            let formField = allFields.find(f => f.id === param.formFieldId);
            if (formField != null) {
                formField.value = formField.getFrontendValueForWorkItem(param);
            }
        }

        for (let field of allFields) {
            field.onClick = async () => {
                await this.scriptButtonClick(this.workItem.activityId, field.userDefinedData.originalCaption as string);
            };
            field.onValueChanged = async byUser => {
                FrontendEnaioMapper.frontendToEnaioValue(field, allFields, this.workItem.parameters);
                if (byUser) {
                    if (field.type === FrontendFieldType.table) {
                        await this.scriptCellValueChanged(this.workItem.activityId, field.userDefinedData.originalCaption as string);
                    }
                    else {
                        await this.runScript(this.workItem.activityId, EnaioWorkflowEventType.valueChanged, field.userDefinedData.originalCaption as string);
                    }
                }
            };
            field.onBeforeAddRow = async () => {
                this.scriptBeforeAddRow(this.workItem.activityId, field.userDefinedData.originalCaption as string);
            }
        }

        this.scriptFormHelper = new FormHelper(this.app, this);
        this.scriptGlobals = {};
        this.scriptScriptingStorage = {};

        (window as any).isWorkflowPortal = true;
        (window as any).wfPortal = new WfPortalInterface(this.app, this.workflowModel.familyPackage.name);
        (window as any).clientScriptFunctions = this.clientScriptFunctions;
        for (let event of this.workflowModel.events) {
            if (event.language == 2 && (event.appId == this.workItem.activityId && (event.clientTypeId == EnaioUtils.CLIENT_ID_ENAIO_WEB || event.clientTypeId == "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF") || event.scriptType == EnaioScriptType.clientGlobal)) {
                let id = this.getClientScriptNameByEvent(event);
                let usesDone = event.type == 1 || event.type == 5;
                let code = "window.clientScriptFunctions['" + id + "'] = (function anonymous(formHelper,globals,scriptingStorage" + (usesDone ? ",done" : "") + ") {\nconsole.log('Skript ausführen: " + event.getSpeakingName() + "');\n" + event.code + "\n})";
                this.loadScript(id, code);
            }
        }

        delete (window as any).clientScriptFunctions;

        await this.runScript("", EnaioWorkflowEventType.clientGlobal);

        return new Promise((resolve, _) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.scriptBeforeOpen(this.workItem.activityId, __ => {
                resolve();
            });
        });
    }

    getClientScriptNameByEvent(event: EnaioWorkflowEventDefinition): string {
        return "clientScript" + event.getSpeakingName();
    }

    loadScript(name: string, code: string): void {
        const script = document.createElement('script');
        script.textContent = code + `\n//# sourceURL=${Utils.replaceAll(name, " ", "_")}`;
        document.body.appendChild(script);
    }

    validate(): boolean {
        let errors = this.form.validate();
        if (errors != null) {
            this.app.messageService.add({ severity: "error", summary: this.app.t("general.invalidInput"), life: 5000, detail: Utils.toString(Utils.objectGetValues(errors)[0])});
            return false;
        }
        return true;
    }

    async forward(test = false): Promise<boolean> {
        if (await this.scriptBeforeForward(this.workItem.activityId) === true) {
            if (!this.validate()) {
                return false;
            }
            if (!test) {
                await this.callForward(true);
            }
            this.app.showToast("info", this.app.t("general.info"), this.app.t("wfs.workflowForwarded"));
            return true;
        }
        return false;
    }

    async save(): Promise<boolean> {
        if (!this.validate()) {
            return false;
        }
        await this.callForward(false);
        this.app.showToast("info", this.app.t("general.info"), this.app.t("wfs.workflowSaved"));
        return true;
    }

    async saveAndWait(): Promise<void> {
        await this.callForward(false);
    }

    async runScriptByName(eventName: string, onDone?: (value: number) => void): Promise<boolean> {
        let fun = this.clientScriptFunctions[eventName];
        if (fun != null) {
            (window as any).formHelper = this.scriptFormHelper;
            this.scriptGlobals.isAwpPortal = true;
            let done = null;
            await fun(this.scriptFormHelper, this.scriptGlobals, this.scriptScriptingStorage, (value: number) => {
                if (onDone != null) {
                    onDone(value);
                }
                done = value > 0;
            });
            return done;
        }
        return null;
    }

    async runScript(appId: string, type: number, param?: string, onDone?: (value: number) => any): Promise<boolean> {
        let event = this.workflowModel.events.find(evt => evt.appId == appId && (evt.clientTypeId == EnaioUtils.CLIENT_ID_ENAIO_WEB || evt.clientTypeId == "FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF" && evt.language == 2) && evt.type == type && (param == null || param == evt.param));
        if (event != null) {
            return this.runScriptByName(this.getClientScriptNameByEvent(event), onDone);
        }
        else {
            onDone?.(null);
            return null;
        }
    }

    async scriptBeforeOpen(appId: string, onDone?: (value: number) => any): Promise<void> {
        await this.runScript(appId, EnaioWorkflowEventType.beforeOpen, null, onDone);
    }

    async scriptBeforeForward(appId: string): Promise<boolean> {
        return new Promise<boolean>((resolve, _) => {
            this.runScript(appId, EnaioWorkflowEventType.beforeForward, null, result => resolve(result >= 0 || result == null));
        });
    }

    async scriptBeforeAddRow(appId: string, param: string): Promise<boolean> {
        return new Promise<boolean>((resolve, _) => {
            this.runScript(appId, EnaioWorkflowEventType.beforeAddRow, param, result => resolve(result >= 0 || result == null));
        });
    }

    async scriptCellValueChanged(appId: string, param: string): Promise<void> {
        await this.runScript(appId, EnaioWorkflowEventType.cellValueChanged, param);
    }

    async scriptButtonClick(appId: string, param: string): Promise<void> {
        await this.runScript(appId, EnaioWorkflowEventType.buttonClick, param);
    }

    private async callForward(send: boolean): Promise<string> {
        let call = Utils.fromPlain(EnaioCallCompleteWorkItem, {
            user: EnaioUserIdentification.byUserName(this.enaioUserName),
            workItemId: this.workItem.id,
            send,
            parameters: this.workItem.parameters.filter(p => !Utils.isArray(p.value.value)).map(p => p.toAssignment()),
            tableParameters: this.workItem.parameters.filter(p => Utils.isArray(p.value.value)).map(p => p.toTableAssignment()),
            documents: this.workItem.files
        });
        return RestEndpoint.main().post().body(call).run("api/wfl/workflow/workitem").getText();
    }

}
